import { createComponent as _$createComponent } from "@opentui/solid";
import { memo as _$memo } from "@opentui/solid";
import { effect as _$effect } from "@opentui/solid";
import { insertNode as _$insertNode } from "@opentui/solid";
import { insert as _$insert } from "@opentui/solid";
import { setProp as _$setProp } from "@opentui/solid";
import { createElement as _$createElement } from "@opentui/solid";
import { useKeyboard, useRenderer, useTerminalDimensions } from "@opentui/solid";
import { batch, createContext, Show, useContext } from "solid-js";
import { useTheme } from "@tui/context/theme";
import { MouseButton, RGBA } from "@opentui/core";
import { createStore } from "solid-js/store";
import { useToast } from "./toast";
import { Flag } from "@/flag/flag";
import { Selection } from "@tui/util/selection";
export function Dialog(props) {
  const dimensions = useTerminalDimensions();
  const {
    theme
  } = useTheme();
  const renderer = useRenderer();
  let dismiss = false;
  return (() => {
    var _el$ = _$createElement("box"),
      _el$2 = _$createElement("box");
    _$insertNode(_el$, _el$2);
    _$setProp(_el$, "onMouseDown", () => {
      dismiss = !!renderer.getSelection();
    });
    _$setProp(_el$, "onMouseUp", () => {
      if (dismiss) {
        dismiss = false;
        return;
      }
      props.onClose?.();
    });
    _$setProp(_el$, "alignItems", "center");
    _$setProp(_el$, "position", "absolute");
    _$setProp(_el$, "left", 0);
    _$setProp(_el$, "top", 0);
    _$setProp(_el$2, "onMouseUp", e => {
      dismiss = false;
      e.stopPropagation();
    });
    _$setProp(_el$2, "paddingTop", 1);
    _$insert(_el$2, () => props.children);
    _$effect(_p$ => {
      var _v$ = dimensions().width,
        _v$2 = dimensions().height,
        _v$3 = dimensions().height / 4,
        _v$4 = RGBA.fromInts(0, 0, 0, 150),
        _v$5 = props.size === "large" ? 80 : 60,
        _v$6 = dimensions().width - 2,
        _v$7 = theme.backgroundPanel;
      _v$ !== _p$.e && (_p$.e = _$setProp(_el$, "width", _v$, _p$.e));
      _v$2 !== _p$.t && (_p$.t = _$setProp(_el$, "height", _v$2, _p$.t));
      _v$3 !== _p$.a && (_p$.a = _$setProp(_el$, "paddingTop", _v$3, _p$.a));
      _v$4 !== _p$.o && (_p$.o = _$setProp(_el$, "backgroundColor", _v$4, _p$.o));
      _v$5 !== _p$.i && (_p$.i = _$setProp(_el$2, "width", _v$5, _p$.i));
      _v$6 !== _p$.n && (_p$.n = _$setProp(_el$2, "maxWidth", _v$6, _p$.n));
      _v$7 !== _p$.s && (_p$.s = _$setProp(_el$2, "backgroundColor", _v$7, _p$.s));
      return _p$;
    }, {
      e: undefined,
      t: undefined,
      a: undefined,
      o: undefined,
      i: undefined,
      n: undefined,
      s: undefined
    });
    return _el$;
  })();
}
function init() {
  const [store, setStore] = createStore({
    stack: [],
    size: "medium"
  });
  const renderer = useRenderer();
  useKeyboard(evt => {
    if (store.stack.length === 0) return;
    if (evt.defaultPrevented) return;
    if ((evt.name === "escape" || evt.ctrl && evt.name === "c") && renderer.getSelection()) return;
    if (evt.name === "escape" || evt.ctrl && evt.name === "c") {
      const current = store.stack.at(-1);
      current.onClose?.();
      setStore("stack", store.stack.slice(0, -1));
      evt.preventDefault();
      evt.stopPropagation();
      refocus();
    }
  });
  let focus;
  function refocus() {
    setTimeout(() => {
      if (!focus) return;
      if (focus.isDestroyed) return;
      function find(item) {
        for (const child of item.getChildren()) {
          if (child === focus) return true;
          if (find(child)) return true;
        }
        return false;
      }
      const found = find(renderer.root);
      if (!found) return;
      focus.focus();
    }, 1);
  }
  return {
    clear() {
      for (const item of store.stack) {
        if (item.onClose) item.onClose();
      }
      batch(() => {
        setStore("size", "medium");
        setStore("stack", []);
      });
      refocus();
    },
    replace(input, onClose) {
      if (store.stack.length === 0) {
        focus = renderer.currentFocusedRenderable;
        focus?.blur();
      }
      for (const item of store.stack) {
        if (item.onClose) item.onClose();
      }
      setStore("size", "medium");
      setStore("stack", [{
        element: input,
        onClose
      }]);
    },
    get stack() {
      return store.stack;
    },
    get size() {
      return store.size;
    },
    setSize(size) {
      setStore("size", size);
    }
  };
}
const ctx = createContext();
export function DialogProvider(props) {
  const value = init();
  const renderer = useRenderer();
  const toast = useToast();
  return _$createComponent(ctx.Provider, {
    value: value,
    get children() {
      return [_$memo(() => props.children), (() => {
        var _el$3 = _$createElement("box");
        _$setProp(_el$3, "position", "absolute");
        _$setProp(_el$3, "onMouseDown", evt => {
          if (!Flag.OPENCODE_EXPERIMENTAL_DISABLE_COPY_ON_SELECT) return;
          if (evt.button !== MouseButton.RIGHT) return;
          if (!Selection.copy(renderer, toast)) return;
          evt.preventDefault();
          evt.stopPropagation();
        });
        _$insert(_el$3, _$createComponent(Show, {
          get when() {
            return value.stack.length;
          },
          get children() {
            return _$createComponent(Dialog, {
              onClose: () => value.clear(),
              get size() {
                return value.size;
              },
              get children() {
                return value.stack.at(-1).element;
              }
            });
          }
        }));
        _$effect(_$p => _$setProp(_el$3, "onMouseUp", !Flag.OPENCODE_EXPERIMENTAL_DISABLE_COPY_ON_SELECT ? () => Selection.copy(renderer, toast) : undefined, _$p));
        return _el$3;
      })()];
    }
  });
}
export function useDialog() {
  const value = useContext(ctx);
  if (!value) {
    throw new Error("useDialog must be used within a DialogProvider");
  }
  return value;
}